import { ChartOptions } from "chart.js";
import uniq from "lodash/uniq";
import { useMemo } from "react";
import { getChartLegendFontConfig } from "../../../../utils/charts.utils";
import { TTasteTestVestingScheduleChartConfig } from "../types";
import { useTasteTestVestingScheduleChartTooltips } from "./useVestingScheduleChartTooltips";
import { numberI18n } from "../../../../utils/number-format";

const {
  theme: { colors, fontFamily },
} = require("@sundae/tailwind-config");

export const useVestingScheduleChartOptions = (
  chartConfig: TTasteTestVestingScheduleChartConfig
): ChartOptions<"line"> => {
  const { currentLocale, responsive, isDark, t, activeTasteTest } = chartConfig;
  const tooltipCallbacks = useTasteTestVestingScheduleChartTooltips(chartConfig);
  const vestingAddresses = uniq(
    activeTasteTest?.vestingSchedule?.map((vesting) => vesting.address) || []
  );

  return useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 4 / 3,
      interaction: {
        mode: "nearest",
        axis: "x",
        intersect: false,
      },
      scales: {
        x: {
          ticks: {
            ...getChartLegendFontConfig(isDark || false, responsive || {}),
          },
          grid: {
            color: "transparent",
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: {
            ...getChartLegendFontConfig(isDark || false, responsive || {}),
            callback: (value) =>
              `${numberI18n(Number(value), { notation: "compact" }, currentLocale)}`,
          },
          grid: { color: "transparent" },
        },
      },
      plugins: {
        tooltip: {
          bodyFont: {
            family: fontFamily.sans[0],
          },
          titleFont: {
            family: fontFamily.sans[0],
          },
          footerFont: {
            family: fontFamily.sans[0],
          },
          callbacks: tooltipCallbacks,
        },
        legend: {
          display: vestingAddresses.length > 1,
          labels: {
            color: isDark ? colors.gray[300] : colors.gray[700],
            font: { family: fontFamily.sans[0] },
            padding: 16,
            boxWidth: 8,
            boxHeight: 8,
          },
          position: "bottom",
          title: {
            padding: {
              top: 32,
            },
            display: true,
            text: t?.("detail.projectDetails.vestingSchedule.chart.legend.title"),
            color: isDark ? colors.gray[200] : colors.black,
            font: {
              family: fontFamily.sans[0],
              weight: "normal",
              size: 13.5,
            },
          },
        },
      },
    }),
    [activeTasteTest?.vestingSchedule, isDark, responsive, t, tooltipCallbacks]
  );
};
